import { Button } from '@design/components/button/Button';
import { Icon } from '@design/components/icon/Icon';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AskAlfred.module.scss';

const SEND_DELAY_MS = 2000;
const TRACKING_SOURCE = POSTHOG_TRACKING_SOURCES.KG_INSIGHT_PAGE;

export interface AskAlfredProps {
  suggestions: string[];
}

export const AskAlfred = ({ suggestions }: AskAlfredProps) => {
  const { t } = useTranslation(['insights']);
  const { navigateToConversation } = useChatNavigation();
  const postQuestion = useChat(state => state.postQuestion);
  const { trackAlfredQuerySent, trackAlfredConversationStarted } =
    usePosthogEvents();

  const [sentSuggestions, setSentSuggestions] = useState<
    Record<string, string>
  >({});
  const [loadingSuggestions, setLoadingSuggestions] = useState<Set<string>>(
    new Set()
  );

  const handleSend = async (suggestion: string) => {
    setLoadingSuggestions(prev => new Set(prev).add(suggestion));

    const conversationId = await postQuestion({ question: suggestion });

    setTimeout(() => {
      setLoadingSuggestions(prev => {
        const next = new Set(prev);
        next.delete(suggestion);
        return next;
      });

      if (conversationId) {
        setSentSuggestions(prev => ({
          ...prev,
          [suggestion]: conversationId,
        }));

        trackAlfredConversationStarted(TRACKING_SOURCE);
        trackAlfredQuerySent({
          source: TRACKING_SOURCE,
          type: 'Standard',
          value: suggestion,
          conversationId,
        });
      }
    }, SEND_DELAY_MS);
  };

  const handleViewInAlfred = (conversationId: string) => {
    navigateToConversation(conversationId, { trackingSource: TRACKING_SOURCE });
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Icon fillColor="var(--text-soft)" iconName="Alfred" size={20} />
        <span className={styles.headerText}>
          {t('insights:catalystGeneration.reflexivityGraph.askAlfred')}
        </span>
      </div>

      <div className={styles.suggestions}>
        {suggestions.map(suggestion => {
          const isLoading = loadingSuggestions.has(suggestion);
          const createdConversationId = sentSuggestions[suggestion];

          return (
            <div
              role="button"
              key={suggestion}
              className={styles.suggestion}
              onClick={
                createdConversationId ? undefined : () => handleSend(suggestion)
              }
            >
              <p className={styles.suggestionText}>{suggestion}</p>
              {createdConversationId ? (
                <Button
                  className={styles.viewButton}
                  label={t(
                    'insights:catalystGeneration.reflexivityGraph.viewInAlfred'
                  )}
                  variant="tertiary"
                  size="xsmall"
                  iconName="Alfred"
                  onClick={() => handleViewInAlfred(createdConversationId)}
                />
              ) : (
                <Button
                  className={styles.sendButton}
                  iconName="ArrowUp"
                  variant="primary"
                  size="xsmall"
                  isLoading={isLoading}
                  inert
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
